import './App.css';

const featureCards = [
  {
    title: 'Emergency cash, minus the chaos',
    body:
      'Give borrowers a fast path to small-dollar relief with clear pricing, guided repayment, and less friction than legacy lenders.',
  },
  {
    title: 'Explainable AI screening',
    body:
      'Use alternative signals and transparent decisioning to review applicants more fairly while keeping risk controls visible.',
  },
  {
    title: 'Community-backed capital',
    body:
      'Create a funding experience that feels human by connecting borrowers, partners, and local lenders around real needs.',
  },
];

const steps = [
  {
    number: '01',
    title: 'Apply in minutes',
    body:
      'Borrowers share the basics, verify income, and describe the need without the paperwork marathon.',
  },
  {
    number: '02',
    title: 'Review with context',
    body:
      'LendLink blends AI scoring with clear borrower signals so approvals feel faster, smarter, and easier to trust.',
  },
  {
    number: '03',
    title: 'Repay with confidence',
    body:
      'Flexible reminders, repayment planning, and visible terms help borrowers stay on track after funding.',
  },
];

const audiences = [
  {
    label: 'Borrowers',
    title: 'A simpler path through unexpected financial needs',
    body:
      'LendLink helps borrowers access affordable short-term funding with clearer terms, faster decisions, and a more manageable repayment experience.',
  },
  {
    label: 'Community partners',
    title: 'A stronger resource for member and employee support',
    body:
      'Credit unions, nonprofits, and employers can connect the people they serve to a modern lending experience built around access, dignity, and financial resilience.',
  },
  {
    label: 'Capital providers',
    title: 'Greater visibility into risk, performance, and impact',
    body:
      'LendLink gives capital providers a clearer view of how funds are deployed, how lending decisions are supported, and how borrower outcomes are strengthened over time.',
  },
];

const proofPoints = [
  'Clear loan terms from the first screen',
  'Mobile-first borrower experience',
  'Decision support built around transparency',
  'Designed for underbanked and emerging-credit users',
];

const policySections = [
  {
    title: 'Information We Collect',
    body:
      'We may collect information you provide directly, such as your name, email address, phone number, and any details you submit through forms, waitlists, demo requests, or support messages. We may also collect technical information such as device type, browser type, IP address, pages visited, and referral sources.',
  },
  {
    title: 'How We Use Information',
    body:
      'We use information to operate and improve the LendLink website, respond to inquiries, evaluate partnership or demo requests, communicate updates, monitor site performance, prevent abuse, and support product development and compliance planning.',
  },
  {
    title: 'How We Share Information',
    body:
      'We do not sell personal information. We may share information with service providers who help us host, analyze, secure, or operate the site, and we may disclose information when required by law, regulation, legal process, or to protect rights, safety, and platform integrity.',
  },
  {
    title: 'Cookies and Analytics',
    body:
      'We may use cookies, similar technologies, and analytics tools to understand traffic patterns, measure engagement, remember preferences, and improve the site experience. You can manage many cookie preferences through your browser settings.',
  },
  {
    title: 'Data Retention and Security',
    body:
      'We retain information only for as long as reasonably necessary for the purposes described in this policy, unless a longer period is required by law. We use administrative, technical, and organizational safeguards designed to protect information, but no internet transmission or storage method is completely secure.',
  },
  {
    title: 'Your Choices',
    body:
      'You may contact us to request access, correction, deletion, or updates to the information you have submitted, subject to applicable law. You may also opt out of non-essential communications by using unsubscribe links where available or by contacting us directly.',
  },
  {
    title: 'Changes to This Policy',
    body:
      'We may update this Privacy Policy from time to time. If we make material changes, we will update the effective date on this page and revise the policy content accordingly.',
  },
];

function Header() {
  const isPrivacyPage = window.location.pathname === '/privacy';

  return (
    <header className="topbar">
      <a className="brand" href={isPrivacyPage ? '/' : '#hero'}>
        <img className="brand-mark" src="/company-logo.png" alt="LendLink logo" />
        <span className="brand-text">
          <strong>LendLink</strong>
          <span>Community lending, redesigned</span>
        </span>
      </a>

      <nav className="topnav" aria-label="Primary">
        {isPrivacyPage ? (
          <>
            <a href="/">Home</a>
            <a href="mailto:mail@lendlink.ai">Contact</a>
          </>
        ) : (
          <>
            <a href="#why">Why it works</a>
            <a href="#how">How it works</a>
            <a href="#audiences">Who it serves</a>
            <a href="#faq">FAQ</a>
          </>
        )}
      </nav>

      <a className="button button-ghost" href={isPrivacyPage ? '/' : '#cta'}>
        {isPrivacyPage ? 'Back to site' : 'Request demo'}
      </a>
    </header>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <p>© 2026 LendLink. Built for clearer, more trusted community lending.</p>
      <div className="footer-links">
        <a href="/privacy">Privacy Policy</a>
        <a href="mailto:mail@lendlink.ai">Contact</a>
      </div>
    </footer>
  );
}

function LandingPage() {
  return (
    <>
      <main>
        <section className="hero" id="hero">
          <div className="hero-copy">
            <p className="eyebrow">Microlending for the modern credit gap</p>
            <h1>Human-centered microlending for real-life emergencies.</h1>
            <p className="hero-text">
              LendLink helps teams imagine a more thoughtful approach to small-dollar
              lending with clearer terms, faster review flows, and stronger borrower trust.
            </p>

            <div className="hero-actions">
              <a className="button button-primary" href="#cta">
                Start with the story
              </a>
              <a className="button button-secondary" href="#how">
                See the flow
              </a>
            </div>

            <ul className="hero-proof" aria-label="Highlights">
              {proofPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </div>

          <div className="hero-panel" aria-hidden="true">
            <div className="pulse-card pulse-card-primary">
              <span>Borrower need</span>
              <strong>$240 for urgent car repair</strong>
              <p>Requested Monday, reviewed with cash-flow context, funded the same day.</p>
            </div>

            <div className="pulse-grid">
              <div className="pulse-card">
                <span>Approval flow</span>
                <strong>3 steps</strong>
                <p>Apply, review, fund.</p>
              </div>
              <div className="pulse-card">
                <span>Decision model</span>
                <strong>AI + human guardrails</strong>
                <p>Built to stay transparent.</p>
              </div>
              <div className="pulse-card accent-card">
                <span>Repayment design</span>
                <strong>Flexible, visible, mobile-first</strong>
              </div>
            </div>
          </div>
        </section>

        <section className="section" id="why">
          <div className="section-heading">
            <p className="eyebrow">Why LendLink</p>
            <h2>A better story for borrowers, lenders, and partners</h2>
          </div>

          <div className="feature-grid">
            {featureCards.map((card) => (
              <article className="feature-card" key={card.title}>
                <h3>{card.title}</h3>
                <p>{card.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section section-alt" id="how">
          <div className="section-heading">
            <p className="eyebrow">How it works</p>
            <h2>A lending experience designed for clarity, speed, and trust</h2>
          </div>

          <div className="step-grid">
            {steps.map((step) => (
              <article className="step-card" key={step.number}>
                <span>{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section" id="audiences">
          <div className="section-heading">
            <p className="eyebrow">Who it serves</p>
            <h2>Built for borrowers, partners, and capital providers</h2>
          </div>

          <div className="audience-grid">
            {audiences.map((item) => (
              <article className="audience-card" key={item.label}>
                <p className="audience-label">{item.label}</p>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section faq-section" id="faq">
          <div className="section-heading">
            <p className="eyebrow">FAQ</p>
            <h2>Common questions about LendLink</h2>
          </div>

          <div className="faq-list">
            <article className="faq-card">
              <h3>What is LendLink?</h3>
              <p>
                LendLink is a community-focused microlending platform designed to
                deliver affordable emergency funding through transparent terms,
                faster decisions, and more explainable underwriting.
              </p>
            </article>
            <article className="faq-card">
              <h3>Who is this for?</h3>
              <p>
                Borrowers, community partners, and capital providers who want a more
                transparent approach to short-term lending and borrower support.
              </p>
            </article>
            <article className="faq-card">
              <h3>What should come after this?</h3>
              <p>
                Next steps include expanding access through borrower and partner
                onboarding, strengthening compliance-ready disclosures, and
                deepening the product experience across the lending journey.
              </p>
            </article>
          </div>
        </section>

        <section className="cta-section" id="cta">
          <div>
            <p className="eyebrow">Next move</p>
            <h2>Build a trusted front door for modern community lending.</h2>
            <p>
              LendLink gives borrowers, partners, and capital providers a clearer
              entry point into a faster, more transparent lending experience.
            </p>
          </div>

          <a className="button button-primary" href="mailto:mail@lendlink.ai">
            Contact LendLink
          </a>
        </section>
      </main>
      <Footer />
    </>
  );
}

function PrivacyPage() {
  return (
    <>
      <main className="policy-shell">
        <section className="policy-hero">
          <p className="eyebrow">Privacy Policy</p>
          <h1>How LendLink collects, uses, and protects information.</h1>
          <p className="policy-intro">
            Effective date: July 6, 2026. This Privacy Policy explains how LendLink
            may collect, use, disclose, and safeguard information when you visit our
            website, request a demo, join a waitlist, or otherwise interact with us online.
          </p>
        </section>

        <section className="policy-card">
          {policySections.map((section) => (
            <article className="policy-section" key={section.title}>
              <h2>{section.title}</h2>
              <p>{section.body}</p>
            </article>
          ))}

          <article className="policy-section">
            <h2>Children’s Privacy</h2>
            <p>
              Our website is not directed to children under 13, and we do not knowingly
              collect personal information from children under 13 through this site.
            </p>
          </article>

          <article className="policy-section">
            <h2>Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or your information, contact
              us at <a href="mailto:mail@lendlink.ai">mail@lendlink.ai</a>.
            </p>
          </article>
        </section>
      </main>
      <Footer />
    </>
  );
}

function App() {
  const isPrivacyPage = window.location.pathname === '/privacy';

  return (
    <div className="landing-shell">
      <Header />
      {isPrivacyPage ? <PrivacyPage /> : <LandingPage />}
    </div>
  );
}

export default App;
